import { useMemo } from "react";
import { MetricCardsContainer } from "@/components/metrics/metric-cards-container";
import type { MetricCardConfig } from "@/components/metrics/types";
import type { ChannelMetrics } from "@/hooks/api/use-channel-metrics";
import { useChannelFilterState } from "@/hooks/use-channel-filter-state";
import { useMetricCardFilters } from "@/hooks/use-metric-card-filters";
import {
  renderMetric as channelRenderMetric,
  DEFAULT_METRIC_ORDER,
  HIDDEN_METRICS,
  METRIC_CONFIG,
  WIP_METRICS,
} from "./channel-metrics-config";

interface ChannelMetricCardsProps {
  metrics: ChannelMetrics | undefined;
  onMetricSelect: (metric: string) => void;
  loadingStates: Record<
    keyof ChannelMetrics,
    { isLoading: boolean; isError: boolean }
  >;
  leftControls?: React.ReactNode;
  showBody?: boolean;
  emptyBody?: React.ReactNode;
}

export function ChannelMetricCards({
  metrics,
  onMetricSelect,
  loadingStates,
  leftControls,
  showBody,
  emptyBody,
}: ChannelMetricCardsProps) {
  const filterState = useChannelFilterState("channels::explorer");
  const {
    displayFilters,
    filterColumns,
    getTotalFilterCount,
    handleClearFilter,
    clearAllFilters,
  } = useMetricCardFilters(filterState);

  // Convert METRIC_CONFIG to the format expected by MetricCardsContainer
  const convertedMetricConfig = useMemo(() => {
    const config: Record<string, MetricCardConfig> = {};

    Object.entries(METRIC_CONFIG).forEach(([key, value]) => {
      config[key] = {
        title: value.title,
        tooltip: value.tooltip,
        category: value.category,
        render: value.render
          ? (val: number | null, options: Record<string, unknown>) => {
              // Convert generic options to the specific format expected by channel metrics
              return value.render!(val, {
                metricKey: options.metricKey as keyof ChannelMetrics,
                metrics: options.metrics as ChannelMetrics | undefined,
              });
            }
          : undefined,
      };
    });

    return config;
  }, []);

  // Wrapper for renderMetric function
  const renderMetric = (
    value: number | null,
    options: Record<string, unknown>,
  ) => {
    return channelRenderMetric(value, {
      metricKey: options.metricKey as keyof ChannelMetrics,
      metrics: options.metrics as ChannelMetrics | undefined,
    });
  };

  return (
    <MetricCardsContainer
      metrics={metrics}
      onMetricSelect={onMetricSelect}
      loadingStates={
        loadingStates as Record<
          string,
          { isLoading: boolean; isError: boolean }
        >
      }
      metricConfig={convertedMetricConfig}
      defaultOrder={DEFAULT_METRIC_ORDER as string[]}
      hiddenMetrics={HIDDEN_METRICS as string[]}
      wipMetrics={WIP_METRICS as string[]}
      storageKey="channels::explorer::metrics::order"
      title="Top-level channel metrics"
      entityScope="channels::explorer"
      entity="channel"
      renderMetric={renderMetric}
      leftControls={leftControls}
      showBody={showBody}
      emptyBody={emptyBody}
      getTotalFilterCount={getTotalFilterCount}
      onResetMetricFilters={clearAllFilters}
      displayFilters={displayFilters}
      filterColumns={filterColumns}
      clearFilter={handleClearFilter}
    />
  );
}
